import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

// ─── Typewriter + Crypto Scramble Hybrid ────────────────────
// Cycles through 4 words with crypto scramble decode effect

const WORDS = ['Decision', 'Prediction', 'Compounding', 'Converging'];

const HOLD_MS = 2800;
const MAX_LEN = Math.max(...WORDS.map(w => w.length));
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
const rand = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];

type Phase = 'holding' | 'deleting' | 'scrambling';

function ScrambleWord() {
    const [visibleText, setVisibleText] = useState(WORDS[0]);
    const [scrambleChars, setScrambleChars] = useState<string[]>([]);
    const [lockedCount, setLockedCount] = useState(0);
    const [phase, setPhase] = useState<Phase>('holding');
    const [showCursor, setShowCursor] = useState(true);
    const wordIdx = useRef(0);
    const timers = useRef<number[]>([]);
    const currentWordRef = useRef(WORDS[0]);

    const clear = useCallback(() => {
        timers.current.forEach(id => clearTimeout(id));
        timers.current = [];
    }, []);

    // Cursor blink
    useEffect(() => {
        const blink = setInterval(() => setShowCursor(v => !v), 530);
        return () => clearInterval(blink);
    }, []);

    const getNextWord = useCallback(() => {
        wordIdx.current = (wordIdx.current + 1) % WORDS.length;
        return WORDS[wordIdx.current];
    }, []);

    const transition = useCallback(() => {
        clear();
        const currentWord = currentWordRef.current;
        const nextWord = getNextWord();

        // ── Phase 1: Typewriter delete ──
        setPhase('deleting');
        let len = currentWord.length;

        const deleteStep = () => {
            len--;
            setVisibleText(currentWord.slice(0, len));
            if (len > 0) {
                timers.current.push(window.setTimeout(deleteStep, 60));
            } else {
                // Pause, then scramble in
                timers.current.push(window.setTimeout(() => startScramble(nextWord), 350));
            }
        };
        timers.current.push(window.setTimeout(deleteStep, 60));

        // ── Phase 2: Crypto scramble in ──
        const startScramble = (target: string) => {
            setPhase('scrambling');
            setLockedCount(0);

            const targetLen = target.length;
            const lockedArr = new Array(targetLen).fill(false);

            // Start scrambling all positions
            const scrambleHandle = window.setInterval(() => {
                setScrambleChars(
                    target.split('').map((ch, i) => (lockedArr[i] ? ch : rand()))
                );
            }, 50);
            timers.current.push(scrambleHandle);

            // Lock chars left-to-right
            for (let i = 0; i < targetLen; i++) {
                const t = window.setTimeout(() => {
                    lockedArr[i] = true;
                    setLockedCount(i + 1);

                    if (i === targetLen - 1) {
                        clearInterval(scrambleHandle);
                        setScrambleChars([]);
                        currentWordRef.current = target;
                        setVisibleText(target);
                        setPhase('holding');
                        timers.current.push(window.setTimeout(transition, HOLD_MS));
                    }
                }, 80 * (i + 1));
                timers.current.push(t);
            }
        };
    }, [clear]);

    useEffect(() => {
        timers.current.push(window.setTimeout(transition, HOLD_MS));
        return clear;
    }, [transition, clear]);

    // ── Render ──
    if (phase === 'scrambling' && scrambleChars.length > 0) {
        return (
            <span className="font-display font-bold tracking-[0.12em] inline-flex items-center">
                {scrambleChars.map((ch, i) => (
                    <span
                        key={i}
                        className={`inline-block text-center ${
                            i < lockedCount ? 'text-white' : 'text-cyan-300/60'
                        } transition-colors duration-100`}
                        style={{ width: '0.68em' }}
                    >
                        {ch}
                    </span>
                ))}
            </span>
        );
    }

    return (
        <span
            className="font-display font-bold tracking-[0.12em] inline-flex items-center"
            style={{ minWidth: `${MAX_LEN * 0.68}em` }}
        >
            <span className="text-white">{visibleText}</span>
            <span
                className={`inline-block w-[2px] h-[0.9em] bg-cyan-400 ml-[1px] ${
                    showCursor ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ transition: 'opacity 0.1s' }}
            />
        </span>
    );
}

export const Hero = () => {
    return (
        <section className="h-screen w-full flex flex-col items-center justify-center relative overflow-hidden bg-transparent">
            {/* Background Ambient Glow - Reduced intensity for subtlety alongside canvas */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[150px] opacity-30 animate-pulse-slow pointer-events-none" />

            <div className="z-10 flex flex-col items-center text-center">
                {/* Backlite Logo Effect */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.8, ease: "easeOut" }}
                    className="relative"
                >
                    {/* The Glow Behind - Refined */}
                    <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full scale-125 opacity-20" />
                    <div className="absolute inset-0 bg-accent/5 blur-2xl rounded-full scale-150 opacity-40" />

                    {/* Main Logo Text - Sharper, bolder */}
                    <div className="flex justify-center gap-[0.2em]">
                        {['K', 'E', 'M', 'P', 'I', 'O', 'N'].map((letter, i) => (
                            <motion.span
                                key={i}
                                initial={{ textShadow: "0 0 0px rgba(0, 170, 255, 0)" }}
                                animate={{
                                    textShadow: [
                                        "0 0 10px rgba(0, 170, 255, 0.2)",
                                        "0 0 20px rgba(0, 170, 255, 0.6)",
                                        "0 0 10px rgba(0, 170, 255, 0.2)"
                                    ]
                                }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 0.2 // Staggered effect
                                }}
                                className="text-5xl md:text-8xl font-display font-bold text-white select-none relative z-10 mix-blend-screen"
                            >
                                {letter}
                            </motion.span>
                        ))}
                    </div>

                    {/* Subtitle - Animated tagline: [word] Intelligence */}
                    <h2 className="text-xs md:text-base font-sans font-light tracking-[0.4em] text-gray-400 mt-6 uppercase flex items-center justify-center gap-3 md:gap-4 whitespace-nowrap">
                        <span className="h-[1px] w-8 bg-gray-700/50"></span>
                        <ScrambleWord />
                        <span>Intelligence</span>
                        <span className="h-[1px] w-8 bg-gray-700/50"></span>
                    </h2>
                </motion.div>
            </div>

            {/* Scroll Indicator */}
            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                onClick={() => {
                    window.scrollTo({
                        top: window.innerHeight,
                        behavior: 'smooth'
                    });
                }}
                className="absolute bottom-12 flex flex-col items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
                aria-label="Scroll to content"
            >
                <span className="text-[10px] uppercase tracking-widest text-gray-600">Explore</span>
                <div className="w-[1px] h-12 bg-gradient-to-b from-gray-600 to-transparent" />
            </motion.button>
        </section>
    );
};
